import { Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { isSandboxEnvironment } from '@config/env'
import { Logo } from './Logo'
import { SandboxBanner } from './SandboxBanner'
import type { ForgotPasswordViewProps } from '../types'

/** Presentational forgot-password form — all copy via `content` props. */
export function ForgotPasswordView({
  content,
  copyright,
  emailError,
  isSubmitting,
  isValid,
  emailInputProps,
  onSubmit,
}: ForgotPasswordViewProps) {
  return (
    <div className="min-h-screen bg-gray-100">
      <SandboxBanner />

      <div
        className={`flex items-center justify-center px-3 py-4 pb-24 sm:p-4 sm:pb-24 ${
          isSandboxEnvironment()
            ? 'min-h-[calc(100vh-40px)]'
            : 'min-h-screen'
        }`}
      >
        <div className="w-full max-w-md rounded-xl bg-white px-5 py-7 shadow-xl sm:p-8 md:p-10">
          <Logo />

          <div className="relative mb-6 flex items-center justify-center sm:mb-8">
            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-gray-200" />
            <div className="relative z-10 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 text-sm font-medium text-teal-dark sm:gap-2 sm:px-3">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              <span>{content.accountAccess}</span>
            </div>
          </div>

          {emailError ? (
            <div
              className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {emailError}
            </div>
          ) : null}

          <div className="mb-5 text-center sm:mb-6">
            <h1 className="text-2xl font-bold text-teal-dark sm:text-3xl">
              {content.title}
            </h1>
            <p className="mt-2.5 text-sm leading-relaxed text-gray-600 sm:mt-3">
              {content.instructions.split('**').map((part, index) =>
                index % 2 === 1 ? (
                  <strong key={index}>{part}</strong>
                ) : (
                  <span key={index}>{part}</span>
                ),
              )}
            </p>
          </div>

          <form onSubmit={onSubmit} noValidate className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-semibold text-gray-700"
              >
                {content.emailLabel}
              </label>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  disabled={isSubmitting}
                  placeholder={content.emailPlaceholder}
                  {...emailInputProps}
                  className={cn(
                    'w-full rounded-md border py-3 pl-11 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500',
                    emailError ? 'border-red-500' : 'border-gray-300',
                    isSubmitting && 'cursor-not-allowed opacity-50',
                  )}
                />
              </div>
              {content.helperText ? (
                <p className="mt-2 text-xs leading-relaxed text-gray-500">
                  {content.helperText}
                </p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !isValid}
              className="w-full rounded-md bg-teal-dark px-6 py-3 font-bold text-white transition-colors hover:bg-teal-darker disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? content.sendingButton : content.sendButton}
            </button>
          </form>

          <div className="mt-5 text-center sm:mt-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-dark hover:text-teal-darker sm:text-base"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {content.backToLogin}
            </Link>
          </div>

          <div className="mt-6 border-t border-gray-200 pt-5 text-center sm:mt-8 sm:pt-6">
            <p className="text-sm text-gray-600">
              {content.rememberedPassword}{' '}
              <Link
                to="/login"
                className="font-semibold text-teal-dark hover:text-teal-darker hover:underline"
              >
                {content.signIn}
              </Link>
            </p>
            <p className="mt-4 text-xs text-gray-400">{copyright}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
